import { createStore, combineReducers } from 'redux'
import { configure } from '../api'
import { Provider } from 'react-redux'
import { IntlProvider } from 'react-intl'
import ReactPlayer from 'react-player'
import {
  selectedTab,
  selectedPin,
  reviewAdded,
} from '../actions/dive-guide-actions'
import {
  selectedCurrency,
  setCurrency,
  pageLoader,
} from '../actions/main-actions'
import {
  currencyDirective,
  hideMobileAppPromotion,
  diveGuidesHeaderFooter,
  breadcrumbsToggle,
  originalTitleTooltip,
} from '../directives'
import ErrorHandler from '../components/ErrorHandler'
import get from 'lodash/get'

// import * as fx from '../libs/money'
import { getLanguage, getTranslations } from 'utils'
import { toast } from 'react-toastify'
import 'babel-polyfill'
import 'whatwg-fetch'
import 'react-toastify/dist/ReactToastify.css'
import HeroLightbox from '../components/liveaboard/HeroLightbox'
import HeroGalleryMobile from '../components/helpers/HeroGalleryMobile'
import PadiSearch from '../components/dive-guides/PadiSearch'
import { getProductCategory } from '../components/dive-guides/gtm-events'
import ConservationOrderSummary from '../components/adventures/ConservationOrderSummary'
import AdventuresSlider from '../components/adventures/AdventuresSlider'
import { convertCurrency } from '../components/helpers/PriceConverter'
import HeroSearch from '../components/homepage/Hero'
import ConservationCategories from '../components/adventures/ConservationCategories'
import ConservationEvents from '../components/adventures/ConservationEvents'
import { setBrandValue } from '../gtm'
import '@formatjs/intl-getcanonicallocales/polyfill'
import '@formatjs/intl-locale/polyfill'
import '@formatjs/intl-pluralrules/polyfill'
import '@formatjs/intl-pluralrules/locale-data/en'
import 'finally-polyfill'
import { createRoot } from 'react-dom/client'

if(process.env.SENTRY_KEY) {
  Sentry.init({
    dsn: process.env.SENTRY_KEY,
    ignoreErrors: [
      'AbortError',
      'UnhandledRejection',
      'Unexpected token o in JSON at position 1',
      'Anonymous function(/gtm.js)',
      "undefined is not an object (evaluating 'd.appendChild')",
      'chrome-extension',
      'gtm',
      'Illegal invocation',
      "Can't find variable: setIOSParameters",
      '(app-modern)',
      'd is undefined',
    ],
  })
}

let store = createStore(
  combineReducers({
    pageLoader,
    selectedPin,
    selectedTab,
    selectedCurrency,
    reviewAdded,
  }),
)

let language = getLanguage()
let messages = {}
toast.configure()

window.store = store
configure(store)

// window.fx = fx

if(language !== 'en') {
  getTranslations(language, renderApp)
} else {
  renderApp()
}

function renderApp(translatedStrings) {
  if(translatedStrings) {
    messages = translatedStrings
  }
  renderElement('padi-search', PadiSearch)
  renderElement('padi-search-mobile', PadiSearch)
  renderElement('hero-lightbox', HeroLightbox, {
    photos: window.photos,
    videos: window.videos,
  })
  renderElement('homepage-hero-search', HeroSearch, {
    isConservation: true,
    worldUrl: window.conservationWorldUrl,
  })
  renderElement('activites-by-category', ConservationCategories, {
    categories: window.conservationCategories,
  })
  renderElement('torchbearer', ConservationEvents, {
    items: window.torchbearerItems,
    region: 'torchbearer-spotlight',
  })
  renderElement('upcoming-events', ConservationEvents, {
    items: window.eventsSpotlightItems,
    region: 'carousel-spotlight',
  })

  const videoContainer = document.getElementById('video-container')
  if(videoContainer) {
    renderElement('video-player', ReactPlayer, {
      url: window.coverVideo && window.coverVideo.url,
      key: '1',
      volume: 1,
      loop: true,
      playing: true,
      controls: false,
      muted: true,
      width: '100%',
      height: `${videoContainer.offsetHeight}px`,
      className: 'player',
      config: {
        vimeo: {
          playerOptions: { background: true },
        },
      },
    })
  }

  if(window.innerWidth < 768) {
    renderElement('collage-photos', HeroGalleryMobile, {
      photos: window.photos,
    })
  }

  // getCoordinates(
  //   (coords) => {
  //     renderElement('activites-nearby', ActivitesNearBy, {
  //       activityId: window.activityId,
  //       coords,
  //       showAllOnFail: window.isExplorationPage,
  //     })
  //   },
  //   () => {
  //     renderElement('activites-nearby', ActivitesNearBy, {
  //       activityId: window.activityId,
  //       showAllOnFail: window.isExplorationPage,
  //     })
  //   },
  // )
  if(window.otherActivites) {
    const impressions = []
    window.otherActivites.forEach((product, index) => {
      const { type } = getProductCategory(50)
      const { currency, price } = product.price
      const { diveCenter } = product
      product.impression = {
        name: product.titleEn || product.title,
        id: product.activityId,
        price: convertCurrency(
          price,
          currency,
          window.currency,
          false,
          true,
        ).toFixed(2),
        brand: setBrandValue(diveCenter),
        category: type,
      }
      product.actionField =
        'Travel Adventures - Conservation Activity Event Page - Other Marine Conservation Activities'
      impressions.push({
        ...product.impression,
        list: product.actionField,
        position: index + 1,
      })
    })
  }

  renderElement('other-conservations', AdventuresSlider, {
    items: window.otherActivites,
    isConservation: true,
  })

  renderElement('activity-order-summary', ConservationOrderSummary, {
    acitivityPricing: window.acitivityPricing,
    activityId: window.activityId,
  })

  hideMobileAppPromotion()
  currencyDirective(function(currency) {
    store.dispatch(setCurrency(currency))
  })
  diveGuidesHeaderFooter()
  breadcrumbsToggle()
  originalTitleTooltip(renderElement)

  if(window.shopName && window.shopId) {
    try {
      const originValue = get(window, 'acitivityPricing.price.origin_value', 0)
      const originCurrency = get(
        window,
        'acitivityPricing.price.origin_currency',
        '',
      )
      const convertedPrice = convertCurrency(
        originValue,
        originCurrency,
        window.currencyCode,
        false,
        true,
      )
      window.dataLayer.push({
        event: 'productDetailView',
        ecommerce: {
          currencyCode: window.currencyCode,
          detail: {
            products: [
              {
                name: window.activityTitle,
                id: +window.activityId,
                price: convertedPrice.toFixed(2),
                brand: setBrandValue({
                  title: window.shopName,
                  affiliateId: window.isProDiver ? false : window.shopId,
                  id: window.shopId,
                }),
                category: `${window.activityType} (Adventures)`,
              },
            ],
          },
        },
      })
    } catch (e) {
      console.error(`Error to push productDetailView`, e)
    }
  }
}

function renderElement(elem, ComponentToRender, props) {
  let element
  if(typeof elem === 'string') {
    element = document.getElementById(elem)
  } else {
    element = elem
  }

  if(element) {
    const root = createRoot(element)
    root.render(
      <IntlProvider
        locale={language === 'ach' ? 'en' : language}
        messages={messages}
        textComponent='span'
      >
        <Provider store={store}>
          <ErrorHandler>
            <ComponentToRender {...props} />
          </ErrorHandler>
        </Provider>
      </IntlProvider>,
    )
  }
}
